// ── Sprint Manager Modal ──

import { api } from '../api.js';
import { openModal, closeModal, showToast } from '../ui.js';
import { escapeHtml } from '../utils.js';
import { groupContainerTitle } from '../lib/task-group-label.js';

const STATUS_LABELS = { active: 'Active', closed: 'Closed' };

// `singular` = already-resolved noun (groupLabelSingular(project.task_group_label)),
// resolved once in openSprintManager() and threaded through — C1420. Row title routes
// through groupContainerTitle() (same always-prefix contract as board-filters.js's
// sprintLabel(), C1395) so a boilerplate stored name ("Sprint 258") shows as "<noun> 258"
// instead of the raw English name, while a genuinely custom name still appends.
function renderSprintRow(sprint, singular) {
  const isClosed = sprint.status === 'closed';
  const canDelete = sprint.task_count === 0;
  const displayName = groupContainerTitle(sprint.number, sprint, singular);
  return `
    <div class="sprint-manager-row" data-sprint-id="${sprint.id}">
      <input class="sprint-manager-name" data-sprint-id="${sprint.id}" data-field="name"
             data-display-name="${escapeHtml(displayName)}"
             value="${escapeHtml(displayName)}" maxlength="255">
      <span class="badge ${isClosed ? 'badge-canceled' : 'badge-completed'}">${STATUS_LABELS[sprint.status]}</span>
      <span class="badge badge-pending">${sprint.task_count} task${sprint.task_count !== 1 ? 's' : ''}</span>
      <div class="sprint-manager-actions">
        <button class="btn btn-ghost btn-sm" data-action="${isClosed ? 'reopen' : 'close'}" data-sprint-id="${sprint.id}">
          ${isClosed ? 'Reopen' : 'Close'}
        </button>
        <button class="btn btn-ghost btn-sm" data-action="delete" data-sprint-id="${sprint.id}"
                ${canDelete ? '' : 'disabled title="Has tasks — move or delete them first"'}
                style="${canDelete ? 'color:var(--c-red)' : ''}">Delete</button>
      </div>
    </div>`;
}

// `groupLabel` = { plural, singular } — passed in by the caller (board.js already
// resolves both via lib/task-group-label.js) rather than fetched here, C1395. Defaults
// keep this callable (e.g. from a stale caller) without crashing.
// Returns the modal's owner token (TPT647) for the caller's teardown.
export function openSprintManager(projectId, { onUpdate, groupLabel } = {}) {
  const plural = (groupLabel && groupLabel.plural) || 'Sprints';
  const singular = (groupLabel && groupLabel.singular) || 'Sprint';
  const body = `
    <form id="sprint-create-form" style="display:flex;gap:0.5rem;margin-bottom:1rem;">
      <input class="form-input" id="sprint-new-name" placeholder="${escapeHtml(singular)} name" required maxlength="255" style="flex:1">
      <button type="submit" class="btn btn-primary btn-sm">+ Create</button>
    </form>
    <div id="sprint-manager-list" class="sprint-manager-list">
      <div class="empty"><span class="spinner"></span></div>
    </div>`;

  return openModal(`Manage ${plural}`, body, {
    onMount(overlay) {
      const listEl = overlay.querySelector('#sprint-manager-list');
      let sprints = [];

      async function load() {
        try {
          const res = await api.get('/projects/' + projectId + '/sprints');
          sprints = res.sprints || [];
          listEl.innerHTML = sprints.length
            ? sprints.map(s => renderSprintRow(s, singular)).join('')
            : `<div class="empty text-muted">No ${escapeHtml(plural.toLowerCase())} yet</div>`;
        } catch (err) {
          listEl.innerHTML = `<div class="empty text-muted">Failed to load ${escapeHtml(plural.toLowerCase())}</div>`;
        }
      }

      load();

      // Create sprint
      overlay.querySelector('#sprint-create-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const input = overlay.querySelector('#sprint-new-name');
        const name = input.value.trim();
        if (!name) return;
        input.disabled = true;
        try {
          await api.post('/projects/' + projectId + '/sprints', { name });
          input.value = '';
          await load();
          onUpdate();
        } catch (err) {
          showToast(err.message || `Failed to create ${singular.toLowerCase()}`, 'error');
        }
        input.disabled = false;
        input.focus();
      });

      // Delegated events on list
      listEl.addEventListener('click', async (e) => {
        const btn = e.target.closest('[data-action]');
        if (!btn) return;
        const action = btn.dataset.action;
        const id = btn.dataset.sprintId;

        if (action === 'close' || action === 'reopen') {
          btn.disabled = true;
          try {
            await api.patch('/projects/' + projectId + '/sprints/' + id, {
              status: action === 'close' ? 'closed' : 'active',
            });
            await load();
            onUpdate();
          } catch (err) {
            showToast(err.message || `Failed to update ${singular.toLowerCase()}`, 'error');
          }
          btn.disabled = false;
        }

        if (action === 'delete') {
          btn.disabled = true;
          try {
            await api.delete('/projects/' + projectId + '/sprints/' + id);
            await load();
            onUpdate();
          } catch (err) {
            showToast(err.message || `Cannot delete ${singular.toLowerCase()}`, 'error');
          }
          btn.disabled = false;
        }
      });

      // Inline name edit on blur. Guard against the derived display name (C1420) —
      // the input shows groupContainerTitle()'s output, not the raw stored name, so a
      // bare click+blur with no actual edit must not PATCH the derived string back in
      // as a "custom" name (that would double-prefix on next render).
      listEl.addEventListener('focusout', async (e) => {
        const input = e.target.closest('input[data-field="name"]');
        if (!input) return;
        const id = input.dataset.sprintId;
        const sprint = sprints.find(s => String(s.id) === id);
        const newName = input.value.trim();
        if (!newName || !sprint || newName === sprint.name || newName === input.dataset.displayName) return;
        try {
          await api.patch('/projects/' + projectId + '/sprints/' + id, { name: newName });
          sprint.name = newName;
          await load();
          onUpdate();
        } catch (err) {
          input.value = sprint.name;
          showToast(err.message || `Failed to rename ${singular.toLowerCase()}`, 'error');
        }
      });
    },
  });
}
